import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface TypingIndicatorProps {
  roomId: string;
  userProfiles: Map<string, any>;
}

interface TypingUser {
  user_id: string;
  typing: boolean;
  timestamp: string;
}

export default function TypingIndicator({ roomId, userProfiles }: TypingIndicatorProps) {
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const { user } = useAuth();

  useEffect(() => {
    if (!roomId || !user) return;

    const channel = supabase.channel(`typing:${roomId}`)
      .on('presence', { event: 'sync' }, () => {
        const presenceState = channel.presenceState();
        const typing = Object.entries(presenceState)
          .flatMap(([key, presences]: [string, any[]]) => presences)
          .filter((presence: any) => 
            presence.typing && 
            presence.user_id !== user.id &&
            Date.now() - new Date(presence.timestamp).getTime() < 5000
          ) as TypingUser[];
        
        setTypingUsers(typing);
      })
      .on('presence', { event: 'join' }, ({ newPresences }) => {
        // Handle new typing users
        console.log('User started typing:', newPresences);
      })
      .on('presence', { event: 'leave' }, ({ leftPresences }) => {
        // Handle users who stopped typing
        console.log('User stopped typing:', leftPresences);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [roomId, user]);

  if (typingUsers.length === 0) return null;

  const getTypingText = () => {
    if (typingUsers.length === 1) {
      const profile = userProfiles.get(typingUsers[0].user_id);
      const name = profile?.first_name || 'Someone';
      return `${name} is typing...`;
    } else if (typingUsers.length === 2) {
      const names = typingUsers.map(tu => {
        const profile = userProfiles.get(tu.user_id);
        return profile?.first_name || 'Someone';
      });
      return `${names.join(' and ')} are typing...`;
    } else {
      return `${typingUsers.length} people are typing...`;
    }
  };

  return (
    <div className="px-4 py-2 text-sm text-muted-foreground italic">
      <div className="flex items-center gap-2">
        <div className="flex gap-1">
          <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
        {getTypingText()}
      </div>
    </div>
  );
}

// Hook for managing typing status
export function useTypingIndicator(roomId: string) {
  const { user } = useAuth();
  const [channel, setChannel] = useState<any>(null);

  useEffect(() => {
    if (!roomId || !user) return;

    const typingChannel = supabase.channel(`typing:${roomId}`);
    setChannel(typingChannel);

    typingChannel.subscribe();

    return () => {
      supabase.removeChannel(typingChannel);
    };
  }, [roomId, user]);

  const setTyping = async (isTyping: boolean) => {
    if (!channel || !user) return;

    if (isTyping) {
      await channel.track({
        user_id: user.id,
        typing: true,
        timestamp: new Date().toISOString()
      });
    } else {
      await channel.untrack();
    }
  };

  return { setTyping };
}